import type JCE from '@chesscom/js-chess-engine';
import type { Game } from 'chessboard/model/game/types';

/**
 * returns the before and after comments for a node
 */
export function getCommentsForNode(
  node: Game.Move.Node,
  lineInitComment?: string,
  ignoreComments?: boolean,
): { beforeComment?: string; afterComment?: string } {
  if (ignoreComments) {
    return {};
  }

  const comments: { beforeComment?: string; afterComment?: string } = {};

  if (node.ids.move === 0 && lineInitComment !== undefined) {
    comments.beforeComment = lineInitComment;
  }

  if (node.comment !== undefined) {
    comments.afterComment = node.comment;
  }

  return comments;
}

/**
 * Determines if a node has multiple or nested variations
 */
export function nodeHasMultipleOrNestedVariations(
  node: Game.Move.Node,
  rawLines: JCE.Line[],
): boolean {
  if (!node.lines || node.lines.length === 0) {
    return false;
  }

  return (
    // multiple variations
    node.lines.length >= 2 ||
    // nested variations
    lineHasVariations(rawLines[node.lines[0]])
  );
}

/**
 * Determines if a jce line has variations.
 */
export function lineHasVariations(line: JCE.Line): boolean {
  return line.some((node) => node.lines && node.lines.length > 0);
}

/**
 * determines if a node is the first node in its line and has a variation
 */
export function nodeIsFirstInLineAndHasVariations(node: Game.Move.Node | undefined): boolean {
  return Boolean(node && node.ids.move === 0 && node.lines && node.lines.length > 0);
}

/**
 * Determines if a node and its variations are rendered as siblings in the move list.
 */
export function nodeHasChildrenRenderedAsSiblings(
  node: Game.Move.Node,
  rawLines: JCE.Line[],
): boolean {
  return (
    nodeIsFirstInLineAndHasVariations(node) || nodeHasMultipleOrNestedVariations(node, rawLines)
  );
}
